import React, { Component, useCallback, useState } from 'react'
import { defineMessages, injectIntl } from 'react-intl'
import {
  CaptionButton,
  Captions,
  Controls,
  FullscreenButton,
  Gesture,
  MediaPlayer,
  MediaProvider,
  MuteButton,
  PlayButton,
  // Poster,
  Spinner,
  Time,
  TimeSlider,
  Track,
  useMediaState,
  VolumeSlider,
} from '@vidstack/react'
import { useDefaultLayoutContext } from '@vidstack/react/player/layouts/default'
import {
  ClosedCaptionsIcon,
  ClosedCaptionsOnIcon,
  FullscreenIcon,
  FullscreenExitIcon,
  MuteIcon,
  PauseIcon,
  PlayIcon,
  ReplayIcon,
  VolumeHighIcon,
  VolumeLowIcon,
} from '@vidstack/react/icons'

import { VIDEO_TYPES } from '@/constants'
import './Video.scss'

const VideoMessages = defineMessages({
  noVideo: {
    defaultMessage: 'No Video',
    description: 'Description displayed when no video is available.',
    id: 'Video.noVideo',
  },
})

const VideoPlayerSpacer = () => {
  return <div className="vds-controls-spacer" />
}

const VideoPlayerLayout = ({ hasCaptions }) => {
  const $viewType = useMediaState('viewType')
  const $textTrack = useMediaState('textTrack')
  const isMatch = $viewType === 'video'
  return (
    <div className="vds-video-layout" data-match={isMatch ? '' : null} data-lg>
      <VideoPlayerGestures />
      <VideoPlayerBufferingIndicator />
      <Controls.Root className="vds-controls">
        <VideoPlayerSpacer />
        <Controls.Group className="vds-controls-group">
          <VideoPlayerTimeSlider />
        </Controls.Group>
        <Controls.Group className="vds-controls-group">
          <VideoPlayerPlayButton />
          <VideoPlayerVolumePopup />
          <VideoPlayerTimeInfo />
          <VideoPlayerSpacer />
          {hasCaptions && (
            <CaptionButton className="vds-button">
              {$textTrack ? (
                <ClosedCaptionsOnIcon className="cc-on-icon vds-icon" />
              ) : (
                <ClosedCaptionsIcon className="cc-off-icon vds-icon" />
              )}
            </CaptionButton>
          )}
          <VideoPlayerFullscreenButton />
        </Controls.Group>
      </Controls.Root>
    </div>
  )
}

const VideoPlayerGestures = () => {
  return (
    <div className="vds-gestures">
      <Gesture
        className="vds-gesture"
        event="pointerup"
        action="toggle:paused"
      />
      <Gesture
        className="vds-gesture"
        event="pointerup"
        action="toggle:controls"
      />
      <Gesture className="vds-gesture" event="dblpointerup" action="seek:-10" />
      <Gesture className="vds-gesture" event="dblpointerup" action="seek:10" />
    </div>
  )
}

const VideoPlayerBufferingIndicator = () => {
  return (
    <div className="vds-buffering-indicator">
      <Spinner.Root className="vds-buffering-spinner">
        <Spinner.Track className="vds-buffering-track" />
        <Spinner.TrackFill className="vds-buffering-track-fill" />
      </Spinner.Root>
    </div>
  )
}

const VideoPlayerTimeSlider = () => {
  const [instance, setInstance] = React.useState(null)
  const [width, setWidth] = React.useState(0)
  const {
    sliderChaptersMinWidth,
    disableTimeSlider,
    seekStep,
    noScrubGesture,
  } = useDefaultLayoutContext()

  const onResize = useCallback(() => {
    const { el } = instance || {}
    const { clientWidth } = el || {}
    if (el) setWidth(clientWidth)
  }, [instance])

  return (
    <TimeSlider.Root
      className="vds-time-slider vds-slider"
      disabled={disableTimeSlider}
      noSwipeGesture={noScrubGesture}
      onResize={onResize}
      keyStep={seekStep}
      ref={setInstance}
    >
      <TimeSlider.Chapters
        className="vds-slider-chapters"
        disabled={width < sliderChaptersMinWidth}
      >
        {(cues, forwardRef) =>
          cues.map((cue) => (
            <div
              className="vds-slider-chapter"
              key={cue.startTime}
              ref={forwardRef}
            >
              <TimeSlider.Track className="vds-slider-track" />
              <TimeSlider.TrackFill className="vds-slider-track-fill vds-slider-track" />
              <TimeSlider.Progress className="vds-slider-progress vds-slider-track" />
            </div>
          ))
        }
      </TimeSlider.Chapters>
      <TimeSlider.Preview className="vds-slider-preview">
        <TimeSlider.Value className="vds-slider-value" />
      </TimeSlider.Preview>
    </TimeSlider.Root>
  )
}

const VideoPlayerTimeInfo = () => {
  const $duration = useMediaState('duration')
  if (!$duration) return null
  return (
    <div className="vds-time-group">
      <Time className="vds-time" type="current" />
      <div className="vds-time-divider">/</div>
      <Time className="vds-time" type="duration" />
    </div>
  )
}

const VideoPlayerFullscreenButton = () => {
  const $fullscreen = useMediaState('fullscreen')
  return (
    <FullscreenButton className="vds-fullscreen-button vds-button">
      {$fullscreen ? (
        <FullscreenExitIcon className="vds-icon" />
      ) : (
        <FullscreenIcon className="vds-icon" />
      )}
    </FullscreenButton>
  )
}

const VideoPlayerVolumeSlider = () => {
  return (
    <VolumeSlider.Root className="vds-volume-slider vds-slider">
      <VolumeSlider.Track className="vds-slider-track" />
      <VolumeSlider.TrackFill className="vds-slider-track-fill vds-slider-track" />
      <VolumeSlider.Thumb className="vds-slider-thumb" />
      <VolumeSlider.Preview className="vds-slider-preview" noClamp>
        <VolumeSlider.Value className="vds-slider-value" />
      </VolumeSlider.Preview>
    </VolumeSlider.Root>
  )
}

const VideoPlayerMuteButton = () => {
  const $muted = useMediaState('muted')
  const $volume = useMediaState('volume')
  return (
    <MuteButton className="vds-mute-button vds-button">
      {$muted || $volume == 0 ? (
        <MuteIcon className="vds-icon" />
      ) : $volume < 0.5 ? (
        <VolumeLowIcon className="vds-icon" />
      ) : (
        <VolumeHighIcon className="vds-icon" />
      )}
    </MuteButton>
  )
}

const VideoPlayerVolumePopup = () => {
  const [_rootEl, setRootEl] = useState(null)
  const [isActive, setIsActive] = useState(false)
  return (
    <div
      className="vds-volume"
      data-active={isActive ? '' : null}
      onMouseEnter={() => !isActive && setIsActive(true)}
      onMouseLeave={() => isActive && setIsActive(false)}
      ref={setRootEl}
    >
      <VideoPlayerMuteButton />
      <div className="vds-volume-popup">
        <VideoPlayerVolumeSlider />
      </div>
    </div>
  )
}

const VideoPlayerPlayButton = () => {
  const $paused = useMediaState('paused')
  const $ended = useMediaState('ended')
  return (
    <PlayButton className="vds-play-button vds-button">
      {$ended ? (
        <ReplayIcon className="replay-icon vds-icon" />
      ) : $paused ? (
        <PlayIcon className="play-icon vds-icon" />
      ) : (
        <PauseIcon className="pause-icon vds-icon" />
      )}
    </PlayButton>
  )
}

// const VideoPlayer = ({ captions, src, thumbnail }) => {
const VideoPlayer = ({ captions, src }) => {
  if (!src || (Array.isArray(src) && !src.length)) return null
  return (
    <MediaPlayer className="VideoPlayer media-player" src={src}>
      <MediaProvider className="VideoPlayer__media-provider">
        {/*thumbnail && (
          <Poster alt="" className="VideoPlayer__poster" src={thumbnail} />
        )*/}
        {captions && (
          <Track default kind="subtitles" src={captions} type="srt" />
        )}
      </MediaProvider>
      {captions && <Captions className="vds-captions" />}
      <VideoPlayerLayout hasCaptions={!!captions} />
    </MediaPlayer>
  )
}

@injectIntl
export default class Video extends Component {
  getVideoEmbedUrl() {
    const {
      entryId: videoEntryId,
      entrypoint: videoEntrypoint,
      entryUrl: videoEntryUrl,
      type: videoType,
      url: videoUrl,
    } = this.props
    let regExpYoutube =
      /(?:youtube\.com\/\S*(?:(?:\/e(?:mbed))?\/|watch\?(?:\S*?&?v=))|youtu\.be\/)([a-zA-Z0-9_-]{6,11})/
    let regExpVimeo =
      /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/

    let match, url

    if (!videoType) return
    if (videoType === VIDEO_TYPES.VIMEO && !videoUrl) return
    if (videoType === VIDEO_TYPES.YOUTUBE && !videoUrl) return
    if (
      (videoType === VIDEO_TYPES.KALTURA ||
        videoType === VIDEO_TYPES.KALTURA_VIDEO) &&
      !(videoEntryId && videoEntryUrl)
    )
      return
    if (videoType === VIDEO_TYPES.VIDEO && !videoEntrypoint) return

    if (videoType === VIDEO_TYPES.YOUTUBE) {
      match = videoUrl.match(regExpYoutube)
      url =
        '//www.youtube.com/embed/' +
        (match && match[1].length === 11 ? match[1] : false)
    }

    if (videoType === VIDEO_TYPES.VIMEO) {
      match = videoUrl.match(regExpVimeo)
      url = '//player.vimeo.com/video/' + (match && match[4] ? match[4] : false)
    }

    if (
      videoType === VIDEO_TYPES.KALTURA ||
      videoType === VIDEO_TYPES.KALTURA_VIDEO
    ) {
      url = videoEntryUrl
    }

    if (videoType === VIDEO_TYPES.VIDEO) {
      // NOTE: currently only supports URL as entrypoint
      // once video_entry Entry supports multiple types,
      // entrypoint will be an ID or URL and should be handled
      // based off the secondary type field
      url = videoEntrypoint
    }

    return url
  }

  render() {
    const {
      entryCaptionsUrl: videoEntryCaptionsUrl,
      entryThumbnailUrl: videoEntryThumbnailUrl,
      intl,
      type: videoType,
    } = this.props
    const videoUrl = this.getVideoEmbedUrl()
    const { formatMessage } = intl
    if (!videoUrl) {
      return <div className="Video">{formatMessage(VideoMessages.noVideo)}</div>
    }
    if (
      videoType === VIDEO_TYPES.KALTURA ||
      videoType === VIDEO_TYPES.KALTURA_VIDEO
    ) {
      return (
        <VideoPlayer
          captions={videoEntryCaptionsUrl}
          src={[videoUrl]}
          thumbnail={videoEntryThumbnailUrl}
        />
      )
    }
    return (
      <div className="Video">
        <iframe
          allowFullScreen
          className="Video-frame"
          frameBorder="0"
          src={videoUrl}
        />
      </div>
    )
  }
}
